import RevealWrapper from "../../base/RevealWrapper";
import { SERVICE_AREAS, BUSINESS } from "../../../mocks/siteData";

export interface ServiceAreasContent {
  eyebrow: string;
  headline: string;
  description: string;
}

interface Props {
  content: ServiceAreasContent;
}

export default function ServiceAreasSection({ content }: Props) {
  const { eyebrow, headline, description } = content;
  const col1 = SERVICE_AREAS.slice(0, 8);
  const col2 = SERVICE_AREAS.slice(8);

  return (
    <section id="service-areas" className="bg-cream-light py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left: text + city list */}
          <div>
            <RevealWrapper direction="left">
              <span className="section-eyebrow">{eyebrow}</span>
              <span className="gold-divider" />
              <h2 className="section-heading text-4xl md:text-5xl font-light mt-0 mb-5 leading-tight">
                {headline}
              </h2>
              <p className="font-sans text-sm text-charcoal/55 leading-relaxed font-light mb-10 max-w-md">
                {description}
              </p>
            </RevealWrapper>

            {/* Cities in two columns */}
            <RevealWrapper delay={0.2}>
              <div className="grid grid-cols-2 gap-x-6 gap-y-0">
                <div className="space-y-0">
                  {col1.map((area) => (
                    <div
                      key={area}
                      className="flex items-center gap-2.5 py-3 border-b border-cream-dark last:border-0 group cursor-default"
                    >
                      <i className="ri-map-pin-2-line text-gold text-sm flex-shrink-0" />
                      <span className="font-sans text-sm font-semibold text-charcoal/85 group-hover:text-charcoal-dark transition-colors">
                        {area}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="space-y-0">
                  {col2.map((area) => (
                    <div
                      key={area}
                      className="flex items-center gap-2.5 py-3 border-b border-cream-dark last:border-0 group cursor-default"
                    >
                      <i className="ri-map-pin-2-line text-gold text-sm flex-shrink-0" />
                      <span className="font-sans text-sm font-semibold text-charcoal/85 group-hover:text-charcoal-dark transition-colors">
                        {area}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </RevealWrapper>

            <RevealWrapper delay={0.35}>
              <div className="mt-8 flex items-start gap-3 text-sm font-sans text-charcoal/60">
                <i className="ri-map-pin-line text-gold mt-0.5 flex-shrink-0" />
                <a
                  href={BUSINESS.directionsUrl}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="hover:text-gold transition-colors cursor-pointer font-light"
                >
                  {BUSINESS.fullAddress}
                </a>
              </div>
            </RevealWrapper>
          </div>

          {/* Right: Large prominent map */}
          <RevealWrapper direction="right" delay={0.2}>
            <div className="relative overflow-hidden" style={{ height: '500px' }}>
              <iframe
                width="100%"
                height="100%"
                frameBorder="0"
                src={BUSINESS.mapEmbed}
                allowFullScreen
                title="The Lash Bar location map — Lehi, Utah"
                aria-label="Google Map showing The Lash Bar at 3397 N 1200 E Suite 110, Lehi, UT 84043"
                className="w-full h-full"
              />
            </div>
            {/* Address card below map */}
            <div className="bg-charcoal-dark px-6 py-5 flex items-center justify-between">
              <div>
                <div className="font-serif text-sm text-white font-light">The Lash Bar</div>
                <div className="font-sans text-xs text-white/40 mt-0.5">{BUSINESS.fullAddress}</div>
              </div>
              <a
                href={BUSINESS.directionsUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="btn-gold text-xs py-2.5 px-5"
              >
                Directions
              </a>
            </div>
          </RevealWrapper>
        </div>
      </div>
    </section>
  );
}
